'use client';
import { createAutocomplete } from '@algolia/autocomplete-core';
import {
  type AutocompleteState,
  getAlgoliaResults,
} from '@algolia/autocomplete-js';
import type { Address, Team } from '@baseball-near-you/prisma';
import {
  Button,
  FormInput,
  Skeleton,
  cn,
  htmlFrom,
} from '@baseball-near-you/ui';
import { env } from '@bb/env';
import { ADDRESSES_INDEX, TEAMS_INDEX } from '@bb/lib/angolia/constants';
import { PATHS } from '@bb/lib/constants';
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import { ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { InstantSearchNext } from 'react-instantsearch-nextjs';

export function OLD() {
  const { watch } = useFormContext();
  const query = watch('query');

  return (
    <FormInput
      name="query"
      label=""
      containerClassName={
        'w-full rounded-sm bg-gray-100/[40%] transition-colors'
      }
      className={cn(
        '!ring-0 active:!border-0 w-full border-0 bg-white/75 text-white transition-all placeholder:text-gray-500 focus:bg-white focus:text-black focus:placeholder:text-black '
      )}
      placeholder="Search for your team"
    />
  );
}

const searchClient = algoliasearch(
  env.NEXT_PUBLIC_ALGOLIA_APP_ID,
  env.NEXT_PUBLIC_SEARCH_ALGOLIA_API_KEY
);

export function HeroSearchInput() {
  return (
    <InstantSearchNext
      indexName={TEAMS_INDEX}
      searchClient={searchClient}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      <div className="flex w-full grow">
        <Autocomplete />
      </div>
    </InstantSearchNext>
  );
}

type AlgoliaTeam = Team & {
  address: Address & {
    fullState: string;
  };
};

function Autocomplete() {
  const { push } = useRouter();

  const [autocompleteState, setAutocompleteState] = useState<
    AutocompleteState<AlgoliaTeam>
  >({} as any);
  const autocomplete = useMemo(
    () =>
      createAutocomplete<AlgoliaTeam>({
        onStateChange({ state }) {
          setAutocompleteState(state as any);
        },
        getSources({ query }) {
          return [
            {
              sourceId: 'teams',
              getItemInputValue({ item }: { item: AlgoliaTeam }) {
                return item.name;
              },
              getItems({ query }: { query: string }) {
                return getAlgoliaResults({
                  searchClient,
                  queries: [
                    {
                      indexName: TEAMS_INDEX,
                      params: {
                        query,
                        hitsPerPage: 4,
                        highlightPreTag: '<span class="font-bold text-black">',
                        highlightPostTag: '</span>',
                        attributesToHighlight: [
                          'name',
                          'address.city',
                          'address.fullState',
                        ],
                      },
                    },
                  ],
                });
              },
              getItemUrl({ item }: { item: AlgoliaTeam }) {
                return item.name;
              },
            },
            {
              sourceId: 'cities',
              getItemInputValue({ item }: { item: AlgoliaTeam }) {
                return item.name;
              },
              getItems({ query }: { query: string }) {
                return getAlgoliaResults({
                  searchClient,
                  queries: [
                    {
                      indexName: ADDRESSES_INDEX,
                      params: {
                        query,
                        hitsPerPage: 1,
                      },
                    },
                  ],
                });
              },
              getItemUrl({ item }: { item: AlgoliaTeam }) {
                return item.name;
              },
            },
          ];
        },
      }),
    []
  );
  const { getEnvironmentProps } = autocomplete;

  const inputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!(formRef.current && panelRef.current && inputRef.current)) {
      return;
    }

    const { onTouchStart, onTouchMove, onMouseDown } = getEnvironmentProps({
      formElement: formRef.current,
      panelElement: panelRef.current,
      inputElement: inputRef.current,
    });

    window.addEventListener('touchstart', onTouchStart);
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('mousedown', onMouseDown);

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('mousedown', onMouseDown);
    };
  }, [getEnvironmentProps, autocompleteState.isOpen]);

  return (
    <div
      className="aa-Autocomplete relative w-full"
      {...autocomplete.getRootProps({})}
    >
      <form
        ref={formRef}
        className="flex h-full w-full items-center justify-between gap-x-2"
        {...(autocomplete.getFormProps({
          inputElement: inputRef.current,
        }) as any)}
      >
        <input
          ref={inputRef}
          className={cn(
            '!ring-0 active:!border-0 h-10 w-full rounded-md border-0 bg-white/75 px-2 text-white outline-hidden transition-all placeholder:text-gray-500 focus:bg-white focus:text-black focus:placeholder:text-black',
            autocompleteState.isOpen ||
              (autocompleteState.query &&
                autocompleteState.query?.length > 0 &&
                'bg-white text-black')
          )}
          {...(autocomplete.getInputProps({
            inputElement: inputRef.current,
          }) as any)}
          placeholder="Find your next team..."
        />
        <Button
          className="h-full bg-white text-red shadow-none transition-all hover:bg-white hover:shadow-xs active:scale-95"
          onClick={() => {
            push(`/search?query=${autocompleteState.query}`);
          }}
        >
          Search
        </Button>
      </form>
      <div
        ref={panelRef}
        className={cn(
          'absolute top-[2.75rem] left-0 z-50 rounded-sm border bg-white text-left shadow-md transition-opacity hover:cursor-pointer',
          autocompleteState.isOpen && autocompleteState.query?.length > 0
            ? 'opacity-100 duration-100'
            : 'h-0 w-0 opacity-0 duration-0'
        )}
        {...(autocomplete.getPanelProps({}) as any)}
      >
        {autocompleteState.status === 'loading' ? (
          <div className="m-1 flex h-64 min-w-68 flex-col items-center justify-between gap-y-1">
            <Skeleton className="h-full w-full" />
            <Skeleton className="h-full w-full" />
            <Skeleton className="h-full w-full" />
            <Skeleton className="h-full w-full" />
          </div>
        ) : (
          autocompleteState.isOpen &&
          autocompleteState.collections.map((collection, index) => {
            const { source, items } = collection;
            return source.sourceId === 'teams' ? (
              <div
                key={`source-${
                  // biome-ignore lint/suspicious/noArrayIndexKey: TODO
                  index
                }`}
              >
                {items.length > 0 && (
                  <ul className="aa-List" {...autocomplete.getListProps()}>
                    {items.map((item) => {
                      const highlightItem: any = (item as any)[
                        '_highlightResult'
                      ];

                      console.log(highlightItem);
                      return (
                        <li
                          key={
                            item.id +
                            item.name +
                            item.address.fullState +
                            item.address.city
                          }
                          className="h-16 w-80 truncate border-b p-2 text-gray-900 last:border-b-0 hover:bg-gray-100"
                          {...(autocomplete.getItemProps({
                            item,
                            source,
                          }) as any)}
                        >
                          <Link prefetch={true} href={`/teams/${item.slug}`}>
                            <div className="flex flex-col">
                              <div className="font-medium text-base">
                                {htmlFrom(highlightItem.name.value)}
                              </div>
                              <div
                                className={cn(
                                  'text-gray-500 text-sm',
                                  !item.address.city &&
                                    !item.address.fullState &&
                                    'text-transparent'
                                )}
                              >
                                {highlightItem.address?.city ? (
                                  <>
                                    {htmlFrom(highlightItem.address.city.value)}
                                    {!!item.address.fullState && ', '}
                                  </>
                                ) : (
                                  ''
                                )}
                                {item.address?.fullState
                                  ? htmlFrom(
                                      highlightItem.address.fullState.value
                                    )
                                  : ''}
                              </div>
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                    {/* {isStateName(items[0].address.fullState) &&
                      !!items[0].address.state && (
                        <li className="flex h-12 w-full items-center rounded-b-md border-t px-2 py-3 hover:bg-gray-50">
                          <Link
                            prefetch={true}
                            href={
                              (items[0] as any)['_highlightResult']?.address
                                ?.city?.matchedWords?.[0]?.length > 0
                                ? `${PATHS.Teams}/us/${items[0].address.state.toLowerCase()}/${(items[0] as any)?.address?.citySlug}`
                                : `${PATHS.Teams}/us/${items[0].address.state.toLowerCase()}`
                            }
                            className="flex w-full items-center justify-between"
                          >
                            <p className="w-full truncate font-semibold text-base text-red">
                              See all teams in{' '}
                              <span className="font-bold text-red">
                                {(items[0] as any)['_highlightResult']?.address
                                  ?.city?.matchedWords?.[0]?.length > 0
                                  ? `${items[0].address.city}, ${items[0].address.state}`
                                  : items[0].address.fullState}
                              </span>
                            </p>
                            <ArrowRight size={20} className="ml-1 text-red" />
                          </Link>
                        </li>
                      )} */}
                  </ul>
                )}
              </div>
            ) : (
              <div
                key={`cities-${
                  // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                  index
                }`}
              >
                <ul>
                  {items.map((item) => {
                    const city = item as any as {
                      city_slug: string;
                      city: string;
                      state: string;
                      postalCode: string;
                    };
                    console.log(city);
                    return (
                      <li
                        key={city.city_slug}
                        className="flex h-12 w-full items-center rounded-b-md border-t px-2 py-3 hover:bg-gray-50"
                      >
                        <Link
                          prefetch={true}
                          href={
                            city.city_slug?.length > 0
                              ? `${PATHS.Teams}/us/${city.state.toLowerCase()}/${city.city_slug}`
                              : `${PATHS.Teams}/us/${city.state.toLowerCase()}`
                          }
                          className="flex w-full items-center justify-between"
                        >
                          <p className="w-full truncate font-semibold text-base text-red">
                            See all teams in{' '}
                            <span className="font-bold text-red">
                              {city.city}, {city.state}
                            </span>
                          </p>
                          <ArrowRight size={20} className="ml-1 text-red" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
